import { HttpLink } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { _getToken } from '@domain/auth/authentication';
import { isBrowser } from '@lib/environment/isBrowser';
import { isProduction } from '@lib/environment/isProduction';
import {
  buildGraphqlHeaders,
  GRAPHQL_URI,
  CORS_OPTIONS,
  BASS_OR_NOT_GRAPHQL_URI,
} from '../../api/rest/shared';
import { getRetryLink } from './shared';
import { ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs';
import { cacheTypePolicies } from './cacheTypePolicies';

// Apollo 4.2 requires module-augmenting DeclareDefaultOptions before client.defaultOptions can
// carry typed shapes. Nested namespaces are the only supported augmentation form here.
// See https://www.apollographql.com/docs/react/data/typescript#declaring-default-options-for-type-safety
/* eslint-disable @typescript-eslint/no-namespace */
declare module '@apollo/client' {
  namespace ApolloClient {
    namespace DeclareDefaultOptions {
      interface Query {
        errorPolicy: 'all';
      }
    }
  }
}
/* eslint-enable @typescript-eslint/no-namespace */

type GraphQLClientType = 'rutilus' | 'bass-or-not';

/*
 * Getting the client and initializing it. We use this in the client and on the server.
 */

const createHttpLinkForType = (type: GraphQLClientType) =>
  new HttpLink({
    uri: type === 'bass-or-not' ? BASS_OR_NOT_GRAPHQL_URI : GRAPHQL_URI,
    ...(type === 'bass-or-not' ? {} : CORS_OPTIONS),
  });

const authLink = new SetContextLink(async (context, _) => {
  const { headers, getAuthToken } = context;
  // The ideal situation is that the getAuthToken function should be passed in the context (
  // coming from useAuth), as it allows for the user's auth state to be updated correctly when the
  // the token is found to be invalid. That said, it's not always possible to do, for example if the
  // query is coming from outside the React render tree. For this case we fallback to using
  // `_getToken`.
  const asyncHeaders = await buildGraphqlHeaders(getAuthToken ?? _getToken);
  return { headers: { ...asyncHeaders, ...headers } };
});

const createApolloLink = (
  clientType: GraphQLClientType,
  useAuthLink: boolean,
  mode: 'server' | 'client',
) => {
  const httpLink = createHttpLinkForType(clientType);
  return useAuthLink
    ? authLink.concat(getRetryLink(clientType, mode)).concat(httpLink)
    : getRetryLink(clientType, mode).concat(httpLink);
};

export const initializeGraphqlClient = ({
  options = {},
  mode,
  clientType = 'rutilus',
  useAuthLink = true,
}: {
  options?: Partial<ApolloClient.Options>;
  mode: 'server' | 'client';
  clientType?: GraphQLClientType;
  useAuthLink?: boolean;
}): ApolloClient => {
  return new ApolloClient({
    cache: new InMemoryCache(cacheTypePolicies),
    link: createApolloLink(clientType, useAuthLink, mode),

    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },

    ...options,

    devtools: {
      enabled: !isProduction() && isBrowser(),
    },
  });
};
